import './styles.scss';

import { Badge, Collapse } from 'react-bootstrap';
import { DATE_FE_FORMAT, TODAY, TODAY_EOD } from '@/constants';
import { Dayts, getResultFromState, handleError, onEnter, printScreen } from '@/utils';
import EnumDropdown, { getItemsFromEnumValues } from '@/components/EnumDropdown';
import Input, { InputWithIcon } from '@/components/Input';
import { IssueTypeEnum, SortDirectionEnum, Trip, TripTableFormatEnum, TripTypeEnum } from '@/models/gen/graphql';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import useTripTableState, { TripSortColumnEnum, getSelectedTrips } from '@/features/Trips/components/TripsTable/hook';

import AirlineIataDropdown from '@/components/AirlineDropdown';
import { AirportGroupDropdown } from '@/components/AirportDropdown';
import ClientDropdown from '@/components/ClientDropdown';
import DateInput from '@/common/DateInput';
import FormButton from '@/components/FormButton';
import HasPermission from '@/components/HasPermission';
import LocationQueryDropdown from '@/components/LocationDropdown';
import QueryFilters from '@/components/QueryFilters';
import RateValuesDropdown from '@/components/RateValuesDropdown';
import React from 'react';
import { TripTypeDropdownMulti } from '@/components/TripTypeDropdown';
import { TripWithCompletionAndRateSearchRaw } from '@/features/Trips/api/services/downloadTripWithCompletionAndRateAsCSV';
import TripsTableFormatDropdown from '@/components/TripsTableFormatDropdown';
import { createPersistentComponentState } from '@/state';
import { customSearchStorage } from '@/state/storage';
import { downloadTripTableAsCSV } from '../../api/services/downloadTripTableWithFormatAsCSV';
import useHotkeys from '@/hooks/useHotkeys';
import useLocale from '@/hooks/useLocale';
import { useNavigate } from 'react-router-dom';
import usePopState from '@/hooks/usePopState';
import useTripSettings from '@/features/Trips/components/TripSettingsModal/hook';

export type TripsFiltersState = {
  from: string;
  to: string;
  airportCode: string[];
  payerProviderId: string;
  doLocationId: string[];
  puLocationId: string[];
  flightNumber: string;
  type: string[];
  issueType: IssueTypeEnum;
  servicerIataAirlineCode: string;
  search: string;
  rateAmount: string;
  format: TripTableFormatEnum;
};
export const initTripsFiltersState: TripsFiltersState = {
  servicerIataAirlineCode: '',
  airportCode: [],
  payerProviderId: '',
  doLocationId: [],
  flightNumber: '',
  from: TODAY,
  puLocationId: [],
  to: TODAY_EOD,
  type: [],
  issueType: null,
  search: '',
  rateAmount: '',
  format: TripTableFormatEnum.Current,
};

const useTripFilters = createPersistentComponentState<TripsFiltersState>('useTripFilters', initTripsFiltersState, {
  live: false,
  storage: customSearchStorage,
  reviver: (key: string, value: any) => {
    switch (key) {
      case 'type': {
        const tripTypeEnumKeys = Object.values(TripTypeEnum);
        return (Array.isArray(value) ? value : [value]).filter((key) => tripTypeEnumKeys.includes(key));
      }
      case 'format':
        return Object.values(TripTableFormatEnum).includes(value) ? value : initTripsFiltersState.format;
      case 'issueType':
        return Object.values(IssueTypeEnum).includes(value) ? value : initTripsFiltersState.issueType;
      default: {
        return value;
      }
    }
  },
});

type TripFiltersProps = {
  onCreate?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onSubmit: (filters: TripsFiltersState) => Promise<void>;
  onReset?: (filters: TripsFiltersState) => Promise<void>;
  sorting?: Array<{ column: TripSortColumnEnum; direction: SortDirectionEnum }>;
  loading?: boolean;
  hasPriorityRows?: boolean;
};
export type TripFiltersRefMethods = {
  quickFilter: (input: TripsFiltersState | ((current: TripsFiltersState) => TripsFiltersState)) => void;
};

const TripFilters = (
  { onCreate, onDelete, onEdit, onSubmit, onReset, sorting, loading, hasPriorityRows }: TripFiltersProps,
  ref: React.ForwardedRef<TripFiltersRefMethods>
): React.JSX.Element => {
  const locale = useLocale<TripTableFormatEnum>({
    [TripTableFormatEnum.United]: 'UA Cancels',
    [TripTableFormatEnum.Priority]: 'Priority',
    [TripTableFormatEnum.ZeroRates]: 'Zero Rates',
  });
  const [state, setState, saveState, rehydrateState] = useTripFilters(({ state, setState, saveState, rehydrate }) => [
    state,
    setState,
    saveState,
    rehydrate,
  ]);
  const setSettingsState = useTripSettings(({ setState }) => setState);
  const selected = useTripTableState(({ state }) => state.selected);
  const [lastQuery, setLastQuery] = useState(state);
  const [expanded, setExpanded] = useState(true);

  // Hotkeys
  useHotkeys('v4_trips_filters', {
    'shift+ctrl+s': {
      name: 'Focus Search Bar',
      description: 'Focuses the Search Bar',
      action: () => (document.querySelector('.TripFilters input[name="search"]') as HTMLElement)?.focus?.(),
    },
    'shift+ctrl+f': {
      name: 'Focus Flight Number',
      description: 'Focuses the Flight Number field',
      action: () => (document.querySelector('.TripFilters input[name="flightNumber"]') as HTMLElement)?.focus?.(),
    },
    'shift+enter': {
      name: 'Search Trips',
      description: 'Search Trips with the Current Filters',
      action: () => (document.querySelector('.TripFilters button[name="submitFilters"]') as HTMLElement)?.click?.(),
    },
    'shift+ctrl+r': {
      name: 'Reset Trips Search',
      description: 'Search Trips with the Default Filters',
      action: () => (document.querySelector('.TripFilters button[name="resetFilters"]') as HTMLElement)?.click?.(),
    },
  });

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setState((current) => ({ ...current, [name]: value })),
    [setState]
  );
  const navigate = useNavigate();

  const handleSubmit = useCallback((): void => {
    const { state } = useTripFilters.getState();
    saveState();
    setLastQuery(state);
    onSubmit(state);
  }, [saveState, onSubmit]);

  // This is exposed via ref so the parent can utilize shortcut functionality
  const handleQuickFilter = useCallback(
    (input: TripsFiltersState | ((current: TripsFiltersState) => TripsFiltersState)): void => {
      const result = getResultFromState(input, state);
      setState(result);
      setLastQuery(result);
      saveState();
      onSubmit(result);
    },
    [state, saveState, onSubmit, setState]
  );

  const handleReset = useCallback((): void => {
    const from = new Dayts().startOf('day').format(Dayts.Format.DATETIME_INPUT);
    const to = new Dayts().endOf('day').format(Dayts.Format.DATETIME_INPUT);
    setState({ ...initTripsFiltersState, from, to });
    setLastQuery({ ...initTripsFiltersState, from, to });
    saveState();
    onReset({ ...initTripsFiltersState, from, to });
  }, [setState, saveState, onReset]);

  useImperativeHandle(ref, () => ({ quickFilter: handleQuickFilter }), [handleQuickFilter]);

  const onExpand = (): void => setExpanded((current: boolean): boolean => !current);

  const handlePopState = useCallback(() => rehydrateState().then(handleSubmit), [rehydrateState, handleSubmit]);
  usePopState(handlePopState);

  useEffect((): void => {
    handlePopState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const selectedForDelete = getSelectedTrips().filter((trip: Trip): boolean => !trip.deletedAt);

  const handleShowSettingsModal = (): void => setSettingsState((current) => ({ ...current, show: true }));

  const getCsv = async (): Promise<void> => {
    try {
      const payload: Partial<TripWithCompletionAndRateSearchRaw> = {
        scheduled: [state.from, state.to],
        airportCode: state.airportCode,
        flightNumber: state.flightNumber,
        payerProviderId: state.payerProviderId,
        rateAmount: state.rateAmount,
        servicerIataAirlineCode: state.servicerIataAirlineCode,
        type: state.type,
        puLocationId: state.puLocationId,
        doLocationId: state.doLocationId,
        issueType: state.issueType,
      };
      await downloadTripTableAsCSV([payload], state.format);
      if (state.format === TripTableFormatEnum.Current && hasPriorityRows) {
        await downloadTripTableAsCSV([payload], TripTableFormatEnum.Priority);
      }
    } catch (err) {
      handleError(err, { notification: { title: 'Export To CSV' } });
    }
  };

  return (
    <QueryFilters className="TripFilters justify-content-between">
      <div className="d-flex flex-column gap-2">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateInput
              name="from"
              format={DATE_FE_FORMAT}
              value={state.from}
              onChange={(from) => {
                handleChange('from')(from);
                if (new Dayts(from).isAfter(state.to)) handleChange('to')(from);
              }}
            />
            <DateInput
              name="to"
              format={DATE_FE_FORMAT}
              value={state.to}
              onChange={(to) => {
                handleChange('to')(to);
                if (new Dayts(to).isBefore(state.from)) handleChange('from')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <AirportGroupDropdown
              name="airportCode"
              value={state.airportCode}
              onChange={(value: string[]) => {
                handleChange('airportCode')(value);
                return false;
              }}
              onEnter={handleSubmit}
              options={{ locale: { 'Select...': 'Airport' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <TripsTableFormatDropdown
              name="format"
              placeholder={locale(state.format)}
              value={state.format || initTripsFiltersState?.format}
              onChange={(val: string): void => {
                if (!val) return;
                handleChange('format')(val);
              }}
              onEnter={handleSubmit}
              options={{
                showClearButton: false,
              }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <Input
              name="flightNumber"
              placeholder="Flight"
              value={state.flightNumber || ''}
              onChange={handleChange('flightNumber')}
              onKeyDown={onEnter((e: React.KeyboardEvent<HTMLInputElement>) => {
                handleChange('flightNumber')((e.target as HTMLInputElement).value);
                handleSubmit();
              })}
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton variant="outline-secondary-subtle" onClick={onExpand}>
              {!expanded ? 'More' : 'Less'}
            </FormButton>
            <FormButton name="submitFilters" variant="success" className="border-white" disabled={loading} onClick={handleSubmit}>
              {(loading && <i className="fa fa-spinner fa-pulse" />) || 'Go'}
            </FormButton>
            <FormButton name="resetFilters" variant="outline-secondary-subtle" onClick={handleReset}>
              Reset
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <InputWithIcon
              icon={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={state.search || ''}
              onChange={handleChange('search')}
              onBlur={handleSubmit}
              onKeyDown={onEnter(handleSubmit)}
              placeholder="Search"
            />
          </QueryFilters.Control>
        </div>
        <Collapse in={expanded}>
          <div>
            <div className="d-flex gap-2">
              <QueryFilters.Input>
                <ClientDropdown
                  name="payerProviderId"
                  value={state.payerProviderId || initTripsFiltersState?.payerProviderId}
                  onChange={handleChange('payerProviderId')}
                  onEnter={handleSubmit}
                  options={{ locale: { 'Select...': 'Clients' } }}
                />
              </QueryFilters.Input>
              <HasPermission name="allowSearchTripsByRate">
                <QueryFilters.Input>
                  <RateValuesDropdown
                    name="rateAmount"
                    placeholder="Rate"
                    value={state.rateAmount}
                    onEnter={handleSubmit}
                    onChange={handleChange('rateAmount')}
                  />
                </QueryFilters.Input>
              </HasPermission>
              <QueryFilters.Input>
                <AirlineIataDropdown
                  onEnter={handleSubmit}
                  name="servicerIataAirlineCode"
                  value={state.servicerIataAirlineCode || initTripsFiltersState?.servicerIataAirlineCode}
                  onChange={handleChange('servicerIataAirlineCode')}
                />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <TripTypeDropdownMulti name="type" value={state.type} onEnter={handleSubmit} onChange={handleChange('type')} />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <EnumDropdown
                  enum={getItemsFromEnumValues(IssueTypeEnum)}
                  value={state.issueType}
                  onChange={handleChange('issueType')}
                  options={{ locale: { 'Select...': 'Issue' } }}
                />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <LocationQueryDropdown
                  name="puLocationId"
                  value={state.puLocationId}
                  onChange={handleChange('puLocationId')}
                  onEnter={handleSubmit}
                  airports={state.airportCode?.length ? state?.airportCode : null}
                  options={{
                    showAirportPrefix: false,
                    locale: { 'Select...': 'Pickups' },
                  }}
                />
              </QueryFilters.Input>
              <QueryFilters.Input>
                <LocationQueryDropdown
                  name="doLocationId"
                  onEnter={handleSubmit}
                  value={state.doLocationId}
                  onChange={handleChange('doLocationId')}
                  airports={state.airportCode?.length ? state.airportCode : null}
                  options={{
                    showAirportPrefix: false,
                    locale: { 'Select...': 'Dropoffs' },
                  }}
                />
              </QueryFilters.Input>
            </div>
          </div>
        </Collapse>
      </div>
      <div className="d-flex justify-content-end gap-2">
        <QueryFilters.Control>
          <FormButton
            maxWidth={1560}
            icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
            name="CREATE_TRIP"
            variant="outline-secondary-subtle"
            onClick={onCreate}
          >
            Create Trip
          </FormButton>
        </QueryFilters.Control>
        <QueryFilters.Control>
          <FormButton
            maxWidth={1560}
            icon={<i className="sv sv-download2 {font-size:1.5rem;}" />}
            variant="outline-secondary-subtle"
            onClick={(): void => navigate('/manifests?type=MANIFEST')}
          >
            Import
          </FormButton>
        </QueryFilters.Control>
        <QueryFilters.Control>
          <FormButton
            maxWidth={1560}
            icon={<i className="sv sv-layers {font-size:1.5rem;}" />}
            name="EDIT_TRIP"
            variant="outline-secondary-subtle"
            onClick={onEdit}
            disabled={!selected.size}
          >
            {selected.size > 1 ? 'Bulk Edit' : 'Edit'}
            {selected.size > 1 && (
              <Badge className="ms-2" pill bg="success">
                {selected.size}
              </Badge>
            )}
          </FormButton>
        </QueryFilters.Control>
        <QueryFilters.Control>
          <FormButton
            name="DELETE_TRIP"
            disabled={!selectedForDelete.length}
            onClick={onDelete}
            variant="outline-secondary-subtle"
            tooltip="Delete Trip(s)"
            icon={<i className="sv sv-trash2 {font-size:1.5rem;}" />}
          />
        </QueryFilters.Control>
        <QueryFilters.Control>
          <FormButton
            variant="outline-secondary-subtle"
            onClick={handleShowSettingsModal}
            icon={<i className="sv sv-cog {font-size:1.5rem;}" />}
            feedback="Trips Page Settings & Preferences"
          />
        </QueryFilters.Control>
        <QueryFilters.Control>
          <FormButton
            variant="outline-secondary-subtle"
            onClick={printScreen}
            icon={<i className="sv sv-printer {font-size:1.5rem;}" />}
            feedback="Print"
          />
        </QueryFilters.Control>
        <QueryFilters.Control>
          <FormButton
            variant="outline-secondary-subtle"
            onClick={getCsv}
            icon={<i className="sv sv-excel {font-size:1.5rem;}" />}
            feedback="Export To CSV"
          />
        </QueryFilters.Control>
      </div>
    </QueryFilters>
  );
};

export default React.forwardRef(TripFilters);
